import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, CalendarDays, Check, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { BookingDialog } from "@/components/BookingDialog";
import { getEvent } from "@/lib/api";

export default function EventDetail() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    getEvent(slug).then(setEvent).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="ytca-container py-24 text-center">
      <h1 className="font-display text-3xl font-semibold">Class not found</h1>
      <Button asChild className="mt-6 rounded-full"><Link to="/events">Back to Classes</Link></Button>
    </div>
  );
  if (!event) return <div className="ytca-container py-24 text-muted-foreground">Loading…</div>;

  return (
    <div>
      <div className="relative h-[42vh] min-h-[320px] w-full overflow-hidden">
        <img src={event.image} alt={event.title} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10" />
        <div className="ytca-container absolute inset-x-0 bottom-0 pb-8">
          <Badge className="rounded-full" variant="secondary">{event.category_label}</Badge>
          <h1 className="mt-3 max-w-3xl font-display text-3xl font-semibold text-white sm:text-5xl">{event.title}</h1>
        </div>
      </div>

      <div className="ytca-container py-10">
        <Link to="/events" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="event-back-link"><ArrowLeft className="h-4 w-4" />All classes</Link>

        <div className="mt-6 grid gap-10 lg:grid-cols-[1.4fr_0.6fr]">
          <div>
            <p className="text-lg leading-relaxed text-foreground/80">{event.description}</p>

            <h2 className="mt-10 font-display text-2xl font-semibold">What you'll learn</h2>
            <ul className="mt-4 space-y-3">
              {(event.experience || []).map((x, i) => (
                <li key={i} className="flex items-start gap-3"><Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><span className="text-foreground/80">{x}</span></li>
              ))}
            </ul>

            {event.who_for && (
              <div className="mt-10 rounded-2xl border border-border bg-secondary/40 p-6">
                <h3 className="font-display text-lg font-semibold">Who it's for</h3>
                <p className="mt-2 text-foreground/80">{event.who_for}</p>
              </div>
            )}
          </div>

          <div className="lg:sticky lg:top-24 lg:self-start">
            <Card className="rounded-2xl border-border bg-card p-6">
              <div className="flex items-baseline justify-between">
                <span className="font-display text-3xl font-semibold" data-testid="event-price">{event.is_free ? "Free" : `₹${event.price}`}</span>
                {!event.is_free && <span className="text-xs text-muted-foreground">per person</span>}
              </div>
              <div className="mt-5 space-y-3 text-sm text-foreground/80">
                <p className="flex items-start gap-2"><CalendarDays className="mt-0.5 h-4 w-4 text-primary" />{event.date}</p>
                <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 text-primary" />{event.location}</p>
              </div>
              <Button onClick={() => setOpen(true)} className="mt-6 w-full rounded-full" size="lg" data-testid="event-book-button">
                {event.is_free ? "Reserve Spot" : "Book / Register"}
              </Button>
            </Card>
          </div>
        </div>
      </div>

      <BookingDialog type="event" item={event} open={open} setOpen={setOpen} />
    </div>
  );
}
